import { Button, Heading, Flex, ButtonGroup } from '@chakra-ui/react';
import { T } from '@transifex/react';
import { t } from '@transifex/native';
import { ReactNode, useMemo } from 'react';
import { useExportMembers } from 'features/members/hooks';
import { mdiAccountPlusOutline, mdiTrayArrowDown } from '@mdi/js';
import { useDrawerStateDispatch } from '../../main/context';
import { openDrawer } from '../../main/context/drawer/actionCreators';
import { Feature, useFeatures } from 'lib/featureFlags/features';
import { RouteSectionNav } from 'features/common/components/RouteSectionNav';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { useLocation, useRouteMatch } from 'react-router-dom';

const Header = ({ children }: { children: ReactNode }) => (
  <Flex
    flexDirection={['column', 'column', 'row']}
    justifyContent='space-between'
    marginBottom={12}
    gap={[6, 6, 0]}
  >
    {children}
  </Flex>
);

const UsersSectionNav = () => {
  const routeMatch = useRouteMatch();
  const location = useLocation();

  const sectionNavItems = useMemo(
    () => [
      {
        id: 'users',
        label: <T _str='Users' />,
        to: `${routeMatch.path}/page/1${location.search}`,
        isActive: (pathname: string) =>
          pathname.startsWith(`${routeMatch.path}/page/`),
      },
      {
        id: 'configuration',
        label: <T _str='Configuration' />,
        to: `${routeMatch.path}/configuration${location.search}`,
        isActive: (pathname: string) =>
          pathname.startsWith(`${routeMatch.path}/configuration`),
      },
    ],
    [routeMatch.path, location.search],
  );

  return (
    <RouteSectionNav
      ariaLabel={t('Users section navigation')}
      items={sectionNavItems}
    />
  );
};

export const MembersHeader = () => {
  const { exportMembers, isLoading: isExportLoading } = useExportMembers();
  const [, drawerDispatch] = useDrawerStateDispatch();
  const features = useFeatures();

  return (
    <>
      <Header>
        <Heading as='h1'>
          <T _str='Users' />
        </Heading>
        <ButtonGroup
          display='flex'
          flexDirection={['column', 'row']}
          gap={2}
          spacing={0}
        >
          <Feature name={'EXPORT_MEMBERS'}>
            <Button
              data-testid='export'
              variant='outline'
              onClick={async () => await exportMembers()}
              isLoading={isExportLoading}
              loadingText='Export'
              leftIcon={<PortalIcon path={mdiTrayArrowDown} />}
              data-behavior-analytics-id='export members'
            >
              <T _str='Export' />
            </Button>
          </Feature>
          <Feature name='INVITATIONS'>
            <Button
              variant='solid'
              data-testid='create_invite_toggle'
              onClick={() => drawerDispatch(openDrawer('invite-drawer'))}
              leftIcon={<PortalIcon path={mdiAccountPlusOutline} />}
              data-behavior-analytics-id='Invite members button - Members page'
            >
              <T _str='Invite' />
            </Button>
          </Feature>
        </ButtonGroup>
      </Header>
      {features.CONTENT_HUB_TOGGLE && <UsersSectionNav />}
    </>
  );
};
